import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Download as DownloadIcon,
  EventBusy as EventBusyIcon,
  LocalOfferOutlined as ActivityIcon,
  TableChart as TableIcon,
  UnfoldLess as CollapseIcon,
  UnfoldMore as ExpandIcon,
} from '@mui/icons-material';
import { useCurrentUser } from '../hooks/useCurrentUser';
import MonthPicker from '../components/monthly/MonthPicker';
import PageHeader, { PageContainer } from '../components/PageHeader';
import MultiSelectFilter, { barButtonSx, SearchFilter, segmentedSx, TogglePill } from '../components/monthly/MultiSelectFilter';
import { MUTED as MUTED_TEXT } from '../components/home/homeTheme';
import PeoplePivotTable from '../components/monthly/PeoplePivotTable';
import { buildMonthDays, formatHours } from '../components/monthly/format';
import { STATUS_STYLE, summarizePerson } from '../components/monthly/completeness';
import { downloadPeopleHoursWorkbook } from '../components/monthly/exportHours';
import { fetchHierarchy, loadPeopleMonthBundle, peekPeopleMonthBundle } from '../utils/monthlyPeopleService';
import { downloadAbsenceCalendarWorkbook, sortPeopleManagerUsersForCalendar } from '../utils/absenceCalendarExport';
import { excludeForecastActivities } from '../utils/worklogFilters';
import { API_ENDPOINTS } from '../config/api.config';

const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
const EMPTY = {};

export default function MonthlyPeopleManagerViewV2() {
  const { userId, userName } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const now = new Date();
  const [year, setYear] = useState(() => Number(searchParams.get('year')) || now.getFullYear());
  const [month, setMonth] = useState(() => Number(searchParams.get('month')) || now.getMonth() + 1);
  const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
  const monthDays = useMemo(() => buildMonthDays(year, month), [year, month]);

  const [people, setPeople] = useState([]);
  const [hierarchyLoading, setHierarchyLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [activityIds, setActivityIds] = useState([]);
  const [search, setSearch] = useState('');
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [sortBy, setSortBy] = useState('name');

  const [bundle, setBundle] = useState({ key: null, data: null });
  const [error, setError] = useState(null);

  const [openTeams, setOpenTeams] = useState(() => new Set());
  const [openProjects, setOpenProjects] = useState(() => new Set());
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);

  const activityKey = activityIds.slice().sort().join(',');
  const rootKey = String(userId);

  // ---- Caricamenti ----
  useEffect(() => {
    if (!userId) return;
    setHierarchyLoading(true);
    fetchHierarchy(userId)
      .then((list) => {
        setPeople(list);
        // Aperti di default i team dei riporti diretti
        setOpenTeams(new Set(list.filter((p) => String(p.managerId) === String(userId) && p.isPeopleManager).map((p) => String(p.id))));
      })
      .catch((e) => setError(`Unable to load your reports: ${e.message}`))
      .finally(() => setHierarchyLoading(false));
  }, [userId]);

  useEffect(() => {
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then((res) => res.json())
      .then((data) => setActivities(excludeForecastActivities(data.activities || [])))
      .catch(() => setActivities([]));
  }, []);

  const peopleIds = useMemo(() => people.map((p) => p.id), [people]);
  const peopleKey = peopleIds.join(',');
  const currentKey = `${peopleKey}|${yearMonth}|${activityKey}`;
  // Mese già in cache (visto prima o precaricato): si mostra subito, senza attesa
  const monthData =
    bundle.key === currentKey ? bundle.data : peopleIds.length ? peekPeopleMonthBundle(peopleIds, yearMonth, activityIds) : null;
  const hours = monthData?.filtered || EMPTY;
  const allActivityHours = monthData?.all || null;
  const absences = monthData?.absences || EMPTY;
  const dataLoading = !!peopleIds.length && !monthData;

  useEffect(() => {
    if (hierarchyLoading || !peopleIds.length) return undefined;
    let cancelled = false;
    // Dopo il mese corrente, precarica in background il precedente e il successivo
    const prefetchNeighbours = () =>
      [-1, 1].forEach((delta) => {
        const d = new Date(year, month - 1 + delta, 1);
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        loadPeopleMonthBundle(peopleIds, ym, activityIds).promise.catch(() => {});
      });
    setError(null);
    loadPeopleMonthBundle(peopleIds, yearMonth, activityIds)
      .promise.then((data) => {
        if (cancelled) return;
        setBundle({ key: currentKey, data });
        prefetchNeighbours();
      })
      .catch((e) => !cancelled && setError(`Unable to load hours: ${e.message}`));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchyLoading, currentKey]);

  // ---- Calcoli ----
  const peopleById = useMemo(() => new Map(people.map((p) => [String(p.id), p])), [people]);
  const childrenByManager = useMemo(() => {
    const map = new Map();
    people.forEach((p) => {
      const key = String(p.managerId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    return map;
  }, [people]);

  const summaries = useMemo(() => {
    const map = new Map();
    people.forEach((p) => {
      const uid = String(p.id);
      const statusDays = (allActivityHours || hours)[uid]?.days;
      const summary = summarizePerson(statusDays, absences[uid], monthDays);
      if (allActivityHours) {
        // Valori mostrati con il filtro attività; stato calcolato su tutte le attività
        const shown = hours[uid]?.days || {};
        summary.statuses = summary.statuses.map((s, i) => {
          const c = shown[monthDays[i].day];
          return { ...s, minutes: (monthDays[i].future ? c?.forecast : c?.actual) || 0 };
        });
        summary.total = Object.values(shown).reduce((sum, c) => sum + c.actual, 0);
      }
      map.set(uid, summary);
    });
    return map;
  }, [people, hours, allActivityHours, absences, monthDays]);

  const teamStats = useMemo(() => {
    const cache = new Map();
    const visit = (managerKey, seen) => {
      if (cache.has(managerKey)) return cache.get(managerKey);
      let count = 0;
      let incomplete = 0;
      (childrenByManager.get(managerKey) || []).forEach((child) => {
        const key = String(child.id);
        if (seen.has(key)) return;
        seen.add(key);
        count += 1;
        if (summaries.get(key)?.missing > 0) incomplete += 1;
        const sub = visit(key, seen);
        count += sub.count;
        incomplete += sub.incomplete;
      });
      const res = { count, incomplete };
      cache.set(managerKey, res);
      return res;
    };
    return (managerKey) => visit(managerKey, new Set([managerKey]));
  }, [childrenByManager, summaries]);

  const compare = useCallback(
    (a, b) => {
      if (sortBy === 'missing') {
        const diff = (summaries.get(String(b.id))?.missing || 0) - (summaries.get(String(a.id))?.missing || 0);
        if (diff) return diff;
      }
      return byName(a, b);
    },
    [sortBy, summaries],
  );

  const flatMode = !!search.trim() || onlyIncomplete;
  const managerName = useCallback(
    (p) => (String(p.managerId) === rootKey ? userName || 'You' : peopleById.get(String(p.managerId))?.name || ''),
    [rootKey, userName, peopleById],
  );

  const rows = useMemo(() => {
    const toRow = (p, depth, extra = {}) => {
      const uid = String(p.id);
      return {
        type: 'person',
        person: p,
        depth,
        summary: summaries.get(uid),
        projects: hours[uid]?.projects,
        projectsOpen: openProjects.has(uid),
        hasReports: false,
        ...extra,
      };
    };
    if (flatMode) {
      const q = search.trim().toLowerCase();
      return people
        .filter((p) => !q || p.name.toLowerCase().includes(q))
        .filter((p) => !onlyIncomplete || summaries.get(String(p.id))?.missing > 0)
        .sort(compare)
        .map((p) => toRow(p, 0, { managerName: `Reports to ${managerName(p)}` }));
    }
    const out = [];
    const seen = new Set([rootKey]);
    const walk = (managerKey, depth) => {
      (childrenByManager.get(managerKey) || [])
        .slice()
        .sort(compare)
        .forEach((p) => {
          const key = String(p.id);
          if (seen.has(key)) return;
          seen.add(key);
          const hasReports = (childrenByManager.get(key) || []).length > 0;
          const teamOpen = hasReports && openTeams.has(key);
          out.push(toRow(p, depth, { hasReports, teamOpen, teamStats: hasReports ? teamStats(key) : null }));
          if (teamOpen) walk(key, depth + 1);
        });
    };
    walk(rootKey, 0);
    return out;
  }, [flatMode, search, onlyIncomplete, people, summaries, hours, openProjects, openTeams, childrenByManager, compare, teamStats, managerName, rootKey]);

  const stats = useMemo(() => {
    let incomplete = 0;
    let missing = 0;
    let total = 0;
    const incompleteByDay = monthDays.map(() => 0);
    const dayTotals = monthDays.map(() => 0);
    summaries.forEach((s) => {
      if (s.missing > 0) incomplete += 1;
      missing += s.missing;
      total += s.total;
      s.statuses.forEach((st, i) => {
        if (st.missing > 0) incompleteByDay[i] += 1;
        dayTotals[i] += st.minutes || 0;
      });
    });
    return { incomplete, missing, total, incompleteByDay, dayTotals };
  }, [summaries, monthDays]);

  // ---- Azioni ----
  const changePeriod = (y, m) => {
    setYear(y);
    setMonth(m);
    setSearchParams({ year: String(y), month: String(m) }, { replace: true });
  };
  const toggleIn = (setter) => (id) =>
    setter((set) => {
      const next = new Set(set);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const managersWithReports = people.filter((p) => (childrenByManager.get(String(p.id)) || []).length > 0).map((p) => String(p.id));

  const resetFilters = () => {
    setActivityIds([]);
    setSearch('');
    setOnlyIncomplete(false);
    setSortBy('name');
    setOpenProjects(new Set());
    changePeriod(now.getFullYear(), now.getMonth() + 1);
  };

  const exportHours = () => {
    setExportAnchor(null);
    const peopleRows = people
      .slice()
      .sort((a, b) => managerName(a).localeCompare(managerName(b)) || byName(a, b))
      .map((p) => {
        const uid = String(p.id);
        const days = {};
        Object.entries(hours[uid]?.days || {}).forEach(([day, c]) => {
          if (c.actual) days[day] = c.actual;
        });
        return { labels: [p.name, managerName(p)], days, extra: { 'Missing (h)': summaries.get(uid)?.missing || 0 } };
      });
    downloadPeopleHoursWorkbook({ year, month, monthDays, peopleRows });
  };

  const exportAbsences = () => {
    setExportAnchor(null);
    setExporting(true);
    try {
      downloadAbsenceCalendarWorkbook({
        scopeLabel: 'People_Manager',
        year,
        month,
        members: sortPeopleManagerUsersForCalendar(people.map((p) => ({ id: p.id, name: p.name, peopleManagerName: managerName(p) }))),
        absencesByUserId: absences,
        secondColumnLabel: 'People Manager',
      });
    } catch (e) {
      setError(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const loading = hierarchyLoading || dataLoading;
  const activityOptions = activities.map((a) => ({ id: a.id, name: a.name }));

  return (
    <PageContainer>
      <PageHeader title={'People overview'}>
        <MonthPicker year={year} month={month} onChange={changePeriod} />
      </PageHeader>

      {/* Filtri + azioni */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <SearchFilter value={search} onChange={setSearch} placeholder="Search people" width={240} />
        <MultiSelectFilter
          label="Activities"
          allLabel="All activities"
          icon={<ActivityIcon />}
          options={activityOptions}
          value={activityIds}
          onChange={setActivityIds}
          width={260}
        />
        <TogglePill label="Only incomplete" checked={onlyIncomplete} onChange={setOnlyIncomplete} />
        <ToggleButtonGroup size="small" exclusive value={sortBy} onChange={(_, v) => v && setSortBy(v)} sx={segmentedSx}>
          <ToggleButton value="name">Sort by name</ToggleButton>
          <ToggleButton value="missing">Most missing</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="text" color="inherit" onClick={resetFilters} sx={{ ...barButtonSx, color: MUTED_TEXT, fontWeight: 500 }}>
          Reset
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          color="inherit"
          startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon fontSize="small" />}
          endIcon={<ArrowDropDownIcon />}
          disabled={exporting || loading || !people.length}
          onClick={(e) => setExportAnchor(e.currentTarget)}
          sx={barButtonSx}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportAnchor}
          open={!!exportAnchor}
          onClose={() => setExportAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={exportHours}>
            <ListItemIcon>
              <TableIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Hours and missing hours" secondary="Excel, one row per person" />
          </MenuItem>
          <MenuItem onClick={exportAbsences}>
            <ListItemIcon>
              <EventBusyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Absences" secondary="Excel, whole hierarchy" />
          </MenuItem>
        </Menu>
      </Box>

      {/* Riepilogo + struttura */}
      {!loading && people.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            <strong style={{ color: 'var(--rt-heading)' }}>{people.length}</strong> people ·{' '}
            {stats.incomplete > 0 ? (
              <Link component="button" type="button" variant="body2" onClick={() => setOnlyIncomplete((v) => !v)} sx={{ color: 'var(--rt-danger)', fontWeight: 700 }}>
                {stats.incomplete} with missing hours
              </Link>
            ) : (
              <span style={{ color: 'var(--rt-green-dark)', fontWeight: 700 }}>everyone complete</span>
            )}
            {stats.missing > 0 && <> · {formatHours(stats.missing)} h missing up to yesterday</>}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {!flatMode && managersWithReports.length > 0 && (
            <>
              <Button size="small" startIcon={<ExpandIcon fontSize="small" />} onClick={() => setOpenTeams(new Set(managersWithReports))} sx={{ textTransform: 'none' }} color="inherit">
                Expand all
              </Button>
              <Button size="small" startIcon={<CollapseIcon fontSize="small" />} onClick={() => setOpenTeams(new Set())} sx={{ textTransform: 'none' }} color="inherit">
                Collapse all
              </Button>
            </>
          )}
        </Box>
      )}

      {activityIds.length > 0 && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
          Hours shown for the selected activities; missing hours are always computed on all activities.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!hierarchyLoading && !people.length ? (
        <Alert severity="info">No people report to you.</Alert>
      ) : !loading && rows.length === 0 ? (
        <Alert severity="info">{onlyIncomplete && !search ? 'Everyone has tracked their hours. 🎉' : 'No people match your search.'}</Alert>
      ) : (
        <PeoplePivotTable
          monthDays={monthDays}
          rows={rows}
          loading={loading}
          onToggleTeam={toggleIn(setOpenTeams)}
          onToggleProjects={toggleIn(setOpenProjects)}
          incompleteByDay={stats.incompleteByDay}
          dayTotals={stats.dayTotals}
          totals={{ total: stats.total, missing: stats.missing }}
        />
      )}

      {/* Legenda */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 1.5, color: 'text.secondary' }}>
        <Typography variant="caption">Click a person to see hours by project.</Typography>
        <Legend color={STATUS_STYLE.under.bg} label="Fewer than 8h" />
        <Legend color={STATUS_STYLE.missing.bg} label="0 = no hours" />
        <Legend color={STATUS_STYLE.partial.bg} label="Partial absence" />
        <Legend color={STATUS_STYLE.absence.bg} label="OFF = full-day absence" />
        <Typography variant="caption">
          <span style={{ color: 'var(--rt-forecast)', fontStyle: 'italic' }}>Blue italic</span> = forecast ·{' '}
          <span style={{ color: 'var(--rt-danger)', fontWeight: 700 }}>red number</span> under a day = people incomplete that day
        </Typography>
      </Box>
    </PageContainer>
  );
}

function Legend({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: color, border: '1px solid var(--rt-hairline)' }} />
      <Typography variant="caption">{label}</Typography>
    </Box>
  );
}
